/* eslint-disable react-hooks/exhaustive-deps */
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import * as React from "react";
import Swiper from "react-id-swiper";
// Import Swiper styles
import { useNavigate } from "react-router";
import { ViewsByTypes } from "src/api";
import Banner from "src/components/Banner";
import FAB from "src/components/FAB";
import InAppExtras from "src/components/InAppExtras";
import { AddBottomSpacing } from "src/components/InAppExtras/styled";
import NavigationDots from "src/components/NavigationDots";
import Spinner from "src/components/Spinner";
import Tabs from "src/components/Tabs";
import { useAppContext } from "src/context/AppContext";
import urls from "src/helpers/urls";
import { Exhibit as ExhibitType } from "src/types";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Content from "./Content";
import {
  BottomActionsContainer,
  Page,
  SwipePageContentWrapper,
  TabContainer,
} from "./styled";

export const Exhibit = () => {
  const sliderRef = React.useRef(null);
  const allExhibitsRef = React.useRef([]);
  const isSliderMoveRef = React.useRef(false);
  const navigate = useNavigate();
  const [showExtras, setShowExtras] = React.useState(false); // State to control the visibility of extras

  const {
    getVisibleExhibits,
    getExhibit,
    selectedExhibitId,
    setSelectedAreaId,
    setSelectedExhibitId,
    setVisitedAreas,
    setVisitedExhibits,
    getSelectedExhibit,
    getVisibleAreas,
    setHasCoordinates,
    hasCoordinates,
    isLoading,
    getVisibleExhibitsByAreaId,
    showFAB,
    org,
    setShowFAB,
    selectedAreaId,
  } = useAppContext();

  // Get necessary data from AppContext

  const [exhibit, setExhibit] = React.useState(null);

  React.useEffect(() => {
    const url = new URL(window.location.href);
    const topicId = url.pathname.split("/").pop();
    const newExhibit = getExhibit(topicId);
    setExhibit(newExhibit);
  }, [window.location.href, org]);

  let areas = getVisibleAreas();

  let allExhibits = [] as Array<{
    id: string;
    areaId: string;
    areaIndex: number;
  }>;

  for (let index = 0; index < areas.length; index++) {
    const currentArea = areas[index];
    let mainVisibleExhibits =
      getVisibleExhibitsByAreaId(currentArea.id).map((exhibit) => ({
        id: exhibit.id,
        areaId: currentArea?.id,
        areaIndex: index,
      })) || [];

    allExhibits = [...allExhibits, ...mainVisibleExhibits];
  }

  allExhibitsRef.current = allExhibits;

  let exhibitSlideIndex = findIndex(allExhibits, { id: selectedExhibitId });
  exhibitSlideIndex = exhibitSlideIndex <= -1 ? 0 : exhibitSlideIndex;
  const exhibitSlide = find(allExhibits, { id: selectedExhibitId });
  const areaIndex = exhibitSlide?.areaIndex || 0;
  const [currentAreaIndex, setCurrentAreaTabIndex] = React.useState(areaIndex);
  const [currentExhibitIndex, setCurrentExhibitIndex] =
    React.useState(exhibitSlideIndex);

  React.useEffect(() => {
    // if exhibit slide index or area index changes then update states
    if (currentAreaIndex !== areaIndex) {
      setCurrentAreaTabIndex(areaIndex);
    }

    if (currentExhibitIndex !== exhibitSlideIndex) {
      setCurrentExhibitIndex(exhibitSlideIndex);
    }
    setSelectedAreaId(areas[areaIndex]?.id);
  }, [
    areaIndex,
    areas,
    currentAreaIndex,
    currentExhibitIndex,
    exhibitSlideIndex,
  ]); // eslint-disable-line

  React.useEffect(() => {
    setCurrentAreaTabIndex(areaIndex);
    setCurrentExhibitIndex(exhibitSlideIndex);
    setExhibit(getSelectedExhibit());
  }, []); // eslint-disable-line

  const onChangeExhibit = (navigateExhibitId, exhibitIndex, newAreaIndex) => {
    setCurrentExhibitIndex(exhibitIndex);
    setCurrentAreaTabIndex(newAreaIndex);

    // Track the visited exhibit in localStorage
    if (navigateExhibitId) {
      setVisitedExhibits(navigateExhibitId);

      // Store visited area ID and update selected area
      const updatedExhibit = getExhibit(navigateExhibitId);
      if (updatedExhibit) {
        setVisitedAreas(updatedExhibit.areaId || "");
        setSelectedAreaId(updatedExhibit.areaId);
        setSelectedExhibitId(navigateExhibitId);
      }
    }

    // here we are pushing url so user can have back button functionality in browser]
    const viewsBy = new URLSearchParams(window.location.search).get("feature");
    const newFeature =
      viewsBy === "share" && !navigateExhibitId ? "share" : ViewsByTypes.menu;
    navigate(urls.exhibit(navigateExhibitId, newFeature));
  };

  const params = {
    initialSlide: currentExhibitIndex,
    shouldSwiperUpdate: true,
    noSwiping: window.screen.width > 959,
    autoHeight: true,
    rebuildOnUpdate: true, // this is needed to update the swiper when the accordion is clicked
    observer: true,
    observeParents: true,
    watchOverflow: true,
    on: {
      sliderMove: () => {
        // track that user is moving slider by hand
        isSliderMoveRef.current = true;
      },
      slideChange: () => {
        if (isSliderMoveRef.current && sliderRef.current) {
          isSliderMoveRef.current = false;
          const swiper = sliderRef.current.swiper;
          const activeIndex = swiper ? swiper.activeIndex : 0;
          const tabObj = allExhibitsRef.current[activeIndex];

          if (tabObj) {
            onChangeExhibit(tabObj.id, activeIndex, tabObj.areaIndex);
          }
        }
      },
    },
  };

  const currentAreaExibits = areas?.[areaIndex]?.exhibits
    ? Object.values(areas?.[areaIndex]?.exhibits)
    : [];
  const exhibitIndexInArea = findIndex(currentAreaExibits, {
    _id: { $oid: selectedExhibitId },
  });

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    // Because of a bug in Safari, we need to set the scroll behavior to "unset" before setting it to "auto"
    // https://forums.developer.apple.com/forums/thread/703294
    // let make sure to set it back to smooth after setting it to unset
    document.documentElement.style.scrollBehavior = "unset";
    document.documentElement.style.scrollBehavior = "auto";
  }, [currentExhibitIndex, selectedExhibitId]);

  // This useEffect is used to set the hasCoordinates, showExtras, and showFAB states based on the exhibit's metadata
  // This for inAppExtras and FAB components
  React.useEffect(() => {
    if (exhibit) {
      const hasCoordinates =
        typeof exhibit?.metadata?.lat === "number" &&
        typeof exhibit?.metadata?.lng === "number";
      const showFAB = !hasCoordinates;

      setHasCoordinates(hasCoordinates);
      setShowFAB(showFAB);
    }
  }, [exhibit]); // eslint-disable-line

  const visibleAreas = getVisibleAreas();
  const [internalIndex, setInternalIndex] = React.useState(exhibitSlideIndex);

  const currentAreaId = visibleAreas[internalIndex]?.id;
  // Build tabItems from visibleAreas

  const [exhibits, setExhibits] = React.useState<ExhibitType[]>([]);
  React.useEffect(() => {
    const visibleExhibits: ExhibitType[] = selectedAreaId
      ? getVisibleExhibits(selectedExhibitId)
      : [];

    setExhibits(visibleExhibits);
  }, [selectedAreaId, selectedExhibitId, currentAreaId]);

  React.useEffect(() => {
    const id = window.location.pathname.split("/")?.[2];
    if (id && exhibits) {
      const foundIndex = exhibits.findIndex((item) => item.id === id);
      if (foundIndex === -1) {
        setInternalIndex(exhibitIndexInArea);
      } else {
        setInternalIndex(foundIndex);
      }
    }
  }, [window.location.pathname, exhibits]);

  React.useEffect(() => {
    const handleAccordionToggle = (e: Event) => {
      const target = e.target as HTMLElement;

      const detailsEl = target.closest(
        "details.mce-accordion"
      ) as HTMLDetailsElement;
      if (!detailsEl) return;

      const swiperContainer = detailsEl.closest(
        ".sc-YOoHS.ibPKlV"
      ) as HTMLElement;
      if (!swiperContainer || !sliderRef.current?.swiper) return;

      const swiper = sliderRef.current.swiper;

      // Only update after the open state actually changes
      requestAnimationFrame(() => {
        // Use resize observer or next frame update to catch height properly
        swiper.updateAutoHeight();
        swiper.update();
      });

      // Also account for CSS transitions
      setTimeout(() => {
        swiper.updateAutoHeight();
        swiper.update();
      }, 300); // match your actual transition duration in CSS
    };

    document.addEventListener("toggle", handleAccordionToggle, true); // 'toggle' is more semantically appropriate
    return () =>
      document.removeEventListener("toggle", handleAccordionToggle, true);
  }, []);

  return isLoading ? (
    <Page>
      <Banner />
      <Spinner $fullHeight />
    </Page>
  ) : (
    <Page>
      <Banner />
      <div>
        {areas.length > 0 && (
          <TabContainer>
            <Tabs />
          </TabContainer>
        )}
        {exhibits.length > 0 && (
          <NavigationDots total={exhibits.length} current={internalIndex} />
        )}
        <SwipePageContentWrapper
          as="div"
          onLoad={() => {
            if (sliderRef && sliderRef.current) {
              sliderRef?.current?.swiper?.update();
            }
          }}
        >
          <Swiper {...params} ref={sliderRef}>
            {allExhibits.map((exhibitInfo, index) => {
              return (
                <div key={index} id={"" + index}>
                  <Content
                    key={exhibitInfo.id}
                    selectedExhibitId={exhibitInfo.id}
                  />
                </div>
              );
            })}
          </Swiper>
        </SwipePageContentWrapper>
        {/* show only if exhibit.enableSharing is true */}
        {(hasCoordinates || showFAB) && (
          <BottomActionsContainer>
            {hasCoordinates && (
              <InAppExtras
                title="Nearby Attractions"
                showingExtras={setShowExtras}
                lat={exhibit?.metadata?.lat}
                lng={exhibit?.metadata?.lng}
                showExtras={showExtras}
              />
            )}
            {!showExtras && <FAB />}
          </BottomActionsContainer>
        )}
        {/* This space is added when the cards are being shown  to ensure proper layout */}
        {showExtras && <AddBottomSpacing />}
      </div>
    </Page>
  );
};
